import { z } from "zod";
import { preferencesSchema } from "./account.ts";
import { brandingSchema } from "./branding.ts";
import { residencyViewSchema } from "./models.ts";
import { entityIdSchema, roleSchema, tenantIdSchema, userIdSchema } from "./tenancy.ts";

/** Double-submit header for cookie-authenticated unsafe requests (value from /auth/session). */
export const CSRF_HEADER = "x-esi-csrf";
/** Optional: act in another entity the user is a member of (validated server-side). */
export const ENTITY_HEADER = "x-esi-entity";

/**
 * Why the presented session no longer works: its own sign-out, revoked by someone or
 * something else, or expired (also any unknown or garbage cookie: never whether an ID existed).
 */
export const SESSION_END_REASONS = ["signed_out", "revoked", "expired"] as const;
export type SessionEndReason = (typeof SESSION_END_REASONS)[number];

export const sessionResponseSchema = z.discriminatedUnion("authenticated", [
  z.object({
    authenticated: z.literal(false),
    /** Set only when a session cookie was presented. */
    ended: z.enum(SESSION_END_REASONS).optional(),
    /**
     * The instance value of `telemetry.clientEvents`, for signed-out pages (openspec change
     * `client-telemetry` D4). Optional for one release (older APIs omit it: send nothing).
     */
    clientTelemetry: z.boolean().optional(),
  }),
  z.object({
    authenticated: z.literal(true),
    principal: z.discriminatedUnion("type", [
      z.object({
        type: z.literal("user"),
        tenantId: tenantIdSchema,
        entityId: entityIdSchema,
        userId: userIdSchema,
        roles: z.array(roleSchema),
      }),
      z.object({
        type: z.literal("operator"),
        operatorId: z.string(),
        /**
         * Who the operator is, from the operators' directory: what the console shows (never the
         * sub). Optional for one release (older APIs omit them).
         */
        email: z.string().nullable().optional(),
        name: z.string().nullable().optional(),
      }),
    ]),
    csrfToken: z.string(),
    /**
     * A federated session should re-federate now (`GET /api/auth/refederate`): 80 % of the
     * tenant's IdP group max age has elapsed since its IdP last asserted the user's groups.
     * Optional for one release (older APIs omit it).
     */
    idpRefreshDue: z.boolean().optional(),
  }),
]);
export type SessionResponse = z.infer<typeof sessionResponseSchema>;

/**
 * `GET /api/me`: the caller's own scope. Names come from the user record and the tenant row
 * (`displayName` null when the directory has none: show `email`). `residency` is the effective
 * `models.residency` at the caller's entity, for the residency note outside a project. No
 * account, model or provider identifiers.
 */
export const meResponseSchema = z.object({
  tenantId: tenantIdSchema,
  entityId: entityIdSchema,
  userId: userIdSchema,
  roles: z.array(roleSchema),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  tenantName: z.string(),
  residency: residencyViewSchema,
  /** Saved language and theme, applied by the SPA once the session is known. */
  preferences: preferencesSchema,
  /**
   * `conversations.standalone` at the user's default entity: they may start conversations
   * outside any project. Optional for one release (older APIs omit it: treat as false).
   */
  standaloneAllowed: z.boolean().optional(),
  /**
   * The tenant's branding (openspec change `tenant-branding`), applied by the SPA once the
   * session is known. Optional for one release (older APIs omit it: apply nothing).
   */
  branding: brandingSchema.optional(),
  /**
   * `telemetry.clientEvents` of the tenant (openspec change `client-telemetry`): the SPA arms
   * its collector only when true. Optional for one release (older APIs omit it: send nothing).
   */
  clientTelemetry: z.boolean().optional(),
  /**
   * `feedback.enabled` of the tenant (openspec change `in-app-feedback`): the SPA shows "Send
   * feedback" only when true. Optional for one release (older APIs omit it: hide the action).
   */
  feedback: z.boolean().optional(),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

const RETURN_BASE = "https://return.invalid";

/**
 * Where to send the browser after sign-in: a same-origin path, normalised (path, query and
 * fragment of the parsed URL, never the raw input), else `/`. Control characters and
 * backslashes are refused outright: browsers strip or rewrite them while parsing, which turns
 * `/\t/evil` or `/\evil` into a protocol-relative `//evil`. Shared by the API and the SPA.
 */
export function safeReturnPath(value: string | null | undefined): string {
  if (!value?.startsWith("/") || value.startsWith("//") || value.length > 2048) {
    return "/";
  }
  for (const char of value) {
    const code = char.charCodeAt(0);
    if (code < 0x20 || code === 0x7f || char === "\\") {
      return "/";
    }
  }
  let url: URL;
  try {
    url = new URL(value, RETURN_BASE);
  } catch {
    return "/";
  }
  if (
    url.origin !== RETURN_BASE ||
    !url.pathname.startsWith("/") ||
    url.pathname.startsWith("//")
  ) {
    return "/";
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * How people on a verified domain bound to an identity provider may sign in (#419, #438, spec
 * `domain-sign-in-modes`), in the order the mode dialog lists them:
 * - `sso_or_esi`, "SSO or ESI password" (the default): anyone may also use an ESI credential;
 * - `sso_admins_esi`, "SSO only": only tenant admins may, as their backup way in (break-glass).
 */
export const DOMAIN_SIGN_IN_MODES = ["sso_or_esi", "sso_admins_esi"] as const;
export const domainSignInModeSchema = z.enum(DOMAIN_SIGN_IN_MODES);
export type DomainSignInMode = z.infer<typeof domainSignInModeSchema>;

/**
 * Every value `tenant_domains.sign_in_mode` may hold (its check constraint): the two modes and
 * the retired `sso_only`, which the previous release may still write (#438). Dropped from the
 * check by a later release (`esi:contract`).
 */
export const STORED_DOMAIN_SIGN_IN_MODES = ["sso_only", "sso_or_esi", "sso_admins_esi"] as const;

/**
 * A mode in a request (#438): the two modes, and for one release the retired `sso_only`, which
 * the previous release's dialog sends and which means "SSO only" now.
 */
export const domainSignInModeInputSchema = z
  .enum(STORED_DOMAIN_SIGN_IN_MODES)
  .transform((m): DomainSignInMode => (m === "sso_only" ? "sso_admins_esi" : m));

/**
 * The mode a stored value means (#438, design T2): only `sso_or_esi` lets everyone in. A domain
 * claimed for an IdP under this release stores it (the default); no recorded mode (a claim the
 * previous release made), the retired `sso_only` and any value another release wrote read as
 * `sso_admins_esi`, the stricter mode, never looser.
 */
export function readDomainSignInMode(stored: string | null | undefined): DomainSignInMode {
  return stored === "sso_or_esi" ? "sso_or_esi" : "sso_admins_esi";
}
